export function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

export function timeToSeconds(timeStr: string): number {
  const [h, m] = (timeStr || '00:00').split(':').map(Number)
  return h * 3600 + m * 60
}

export function dateTimeToTrassirFormat(date: string, time: string): string {
  const d = date.replace(/-/g, '')
  const t = (time || '00:00').replace(/:/g, '') + '00'
  return `${d}T${t}`
}

/**
 * Стена времени в TZ сервера Trassir → микросекунды UNIX (как требует JIT SDK).
 * utcOffsetMinutes: UTC+3 → 180
 */
export function dateTimeToMicroseconds(
  date: string,
  time: string,
  utcOffsetMinutes = 0,
): number {
  const [year, month, day] = date.split('-').map(Number)
  const [hours, minutes] = (time || '00:00').split(':').map(Number)
  const asUtcMs = Date.UTC(year, month - 1, day, hours, minutes, 0, 0)
  return (asUtcMs - utcOffsetMinutes * 60 * 1000) * 1000
}

/** UNIX µs → YYYYMMDDTHHMMSS в TZ сервера Trassir (по умолчанию MSK = +180). */
export function microsecondsToTrassirFormat(
  us: number,
  utcOffsetMinutes = 180,
): string {
  const unixMs = Math.floor(Number(us) / 1000)
  if (!Number.isFinite(unixMs)) return ''
  const wall = new Date(unixMs + Number(utcOffsetMinutes || 0) * 60 * 1000)
  if (Number.isNaN(wall.getTime())) return ''
  const pad2 = (n: number) =>
    String(Math.max(0, Math.min(99, Math.floor(n)))).padStart(2, '0').slice(-2)
  const y = wall.getUTCFullYear()
  const mo = pad2(wall.getUTCMonth() + 1)
  const d = pad2(wall.getUTCDate())
  const h = pad2(wall.getUTCHours())
  const mi = pad2(wall.getUTCMinutes())
  const s = pad2(wall.getUTCSeconds())
  const out = `${y}${mo}${d}T${h}${mi}${s}`
  return /^\d{8}T\d{6}$/.test(out) ? out : ''
}

export const POS_CLIP_PRE_SEC = 6
export const POS_CLIP_POST_SEC = 8
/** Жёсткий минимум длины клипа: короткие окна (5 с) рвут MJPEG → FFmpeg -138. */
export const POS_CLIP_MIN_SEC = 12

/**
 * Окно архива вокруг события: ±pre/post.
 * Соседи слегка ужимают окно, но не короче MIN (иначе архив не успевает отдать кадры).
 */
export function buildNonOverlappingClip(
  eventTsUs: number,
  allEventTsUs: number[],
  opts?: {
    preSec?: number
    postSec?: number
    minSec?: number
    utcOffsetMinutes?: number
  },
): { startUs: number; stopUs: number; start: string; stop: string } | null {
  const eventUs = Math.floor(Number(eventTsUs))
  if (!Number.isFinite(eventUs) || eventUs <= 0) return null

  const preSec = opts?.preSec ?? POS_CLIP_PRE_SEC
  const postSec = opts?.postSec ?? POS_CLIP_POST_SEC
  const minSec = opts?.minSec ?? POS_CLIP_MIN_SEC
  const utcOffsetMinutes = opts?.utcOffsetMinutes ?? 180

  const sorted = [
    ...new Set(
      allEventTsUs
        .map((t) => Math.floor(Number(t)))
        .filter((t) => Number.isFinite(t) && t > 0),
    ),
  ].sort((a, b) => a - b)

  let prev: number | null = null
  let next: number | null = null
  for (const t of sorted) {
    if (t < eventUs) prev = t
    else if (t > eventUs) {
      next = t
      break
    }
  }

  const preUs = preSec * 1_000_000
  const postUs = postSec * 1_000_000
  const minUs = minSec * 1_000_000

  let startUs = eventUs - preUs
  let stopUs = eventUs + postUs

  // Мягкое ужатие к середине с соседом
  if (prev != null) {
    startUs = Math.max(startUs, Math.floor((prev + eventUs) / 2))
  }
  if (next != null) {
    stopUs = Math.min(stopUs, Math.floor((eventUs + next) / 2))
  }

  // Жёсткий минимум длины — приоритетнее non-overlap (иначе Trassir рвёт HTTP)
  if (stopUs - startUs < minUs) {
    const half = Math.floor(minUs / 2)
    startUs = eventUs - half
    stopUs = eventUs + (minUs - half)
  }

  if (stopUs <= startUs) {
    startUs = eventUs
    stopUs = eventUs + minUs
  }

  let start = microsecondsToTrassirFormat(startUs, utcOffsetMinutes)
  let stop = microsecondsToTrassirFormat(stopUs, utcOffsetMinutes)
  if (!start || !stop) return null
  if (stop <= start) {
    stopUs = startUs + minUs
    stop = microsecondsToTrassirFormat(stopUs, utcOffsetMinutes)
    if (!stop || stop <= start) return null
  }

  return { startUs, stopUs, start, stop }
}

/**
 * Клип архива вокруг ActivePOS-события.
 * SDK: event_timestamp — local wall as UTC → wall YYYYMMDDTHHMMSS без +utcOffset сервера.
 */
export function buildPosArchiveClip(
  eventTsUs: number,
  allEventTsUs: number[] = [],
  opts?: {
    preSec?: number
    postSec?: number
    minSec?: number
  },
): { startUs: number; stopUs: number; start: string; stop: string } | null {
  return buildNonOverlappingClip(eventTsUs, allEventTsUs, {
    ...opts,
    utcOffsetMinutes: 0,
  })
}

/** Показ ActivePOS event_ts (local-as-UTC) без сдвига Europe/Moscow. */
export function formatPosEventTime(tsUs: number | null | undefined): string {
  if (tsUs == null || !Number.isFinite(tsUs)) return '—'
  const d = new Date(Number(tsUs) / 1000)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('ru-RU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

export function trassirWsUrl(serverId: number, streamId?: string): string {
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  // Control JSON — /trassir-ws; binary MPEG-TS (JSMpeg) — /trassir-video-ws (media worker)
  if (streamId) {
    return `${wsProtocol}//${window.location.host}/trassir-video-ws?serverId=${serverId}&streamId=${encodeURIComponent(streamId)}`
  }
  return `${wsProtocol}//${window.location.host}/trassir-ws?serverId=${serverId}`
}
